import { createPortal } from 'react-dom'
import { memo, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'

import { ColorPicker as RfColorPicker } from '@renderforest/rf-components'
import { userSelectors } from 'store/features/user/selectors'
import { useOnClickOutside } from 'hooks/useOnClickOutside'

import * as Styled from './styled'
import { useCalcColorPickerTopLeftCoords } from './hooks'

export interface IColorPickerPopoverProps {
  isOpen: boolean
  onClose: () => void
  parentRef: null | HTMLDivElement
  currentColor: string | null
  onChangeCompleted: (hexColor: string) => void
  suggestedColors?: string[] | null
  contentHeight?: number
  borderRadius?: number
}

export const ColorPickerPopover = memo(function ColorPickerPopover(
  props: IColorPickerPopoverProps
) {
  const {
    isOpen,
    onClose,
    parentRef,
    currentColor,
    onChangeCompleted,
    suggestedColors,
    contentHeight = 330,
    borderRadius = 12
  } = props

  const isUserLoggedIn = useSelector(userSelectors.isUserLoggedIn)
  const [colorPickerWrapperRef, setColorPickerWrapperRef] =
    useState<null | HTMLDivElement>(null)

  useOnClickOutside([parentRef, colorPickerWrapperRef], onClose)

  const { top, left } = useCalcColorPickerTopLeftCoords(
    parentRef,
    colorPickerWrapperRef
  )

  const onColorChangeCompleted = useCallback(
    (hexColor: string) => {
      onChangeCompleted(hexColor)
    },
    [onChangeCompleted]
  )

  if (!isOpen) return null

  return createPortal(
    <Styled.ColorPickerWrapper
      ref={setColorPickerWrapperRef}
      left={left}
      top={top}
      $borderRadius={borderRadius}
    >
      <RfColorPicker
        isDarkMode={false}
        currentColor={currentColor}
        onChangeCompleted={hexColor => {
          onColorChangeCompleted(hexColor)
        }}
        isLoggedIn={isUserLoggedIn}
        suggestedColors={suggestedColors ?? undefined}
        contentHeight={contentHeight}
        openAuthModal={window.openRFAuthModal}
      />
    </Styled.ColorPickerWrapper>,
    document.body
  )
})
